import { useEffect, useState } from "react";
import {
    FormControl,
    FormControlLabel,
    FormHelperText,
    Stack,
    Avatar,
    Box,
    Checkbox,
    Grid,
    Button,
    TextField,
    Container,
    CssBaseline,
    Alert,
    Link,
} from "@mui/material";
import ImageUploading, { ImageType } from "react-images-uploading";
import { InfinitySpin } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PasswordInput from "../../common/components/PasswordInput";
import { useAppDispatch, useAppSelector } from "../../common/redux/reduxHooks";
import { RootState } from "../../common/redux/store";
import { RouteAPI } from "../../common/service/apiService";
import { AvatarResponse } from "../../structures/model/StructureModel";
import { clearPicture } from "../../structures/redux/signUpSlice";
import { getPictureFromProfilImage } from "../../utils/FormatUtils";
import { language, LanguageType } from "../../utils/LanguageUtils";
import { RouteUtils } from "../../utils/RouteUtils";
import { SignUpData } from "../../login/model/SignUpData";
import { RouteApp } from "../../common/model/RouteApp";
import { Modal } from "../../common/components/Modal";

import {
    emptyUserProfessionnel,
    UserProfessionnelResponse,
} from "../../professionnels/model/ProfessionnelModel";
import {
    postAvatar,
    signUpProfessionnel,
} from "../../professionnels/redux/signUpAction";
import { Country } from "../redux/countryActions";
import CountrySelect from "./CountryInput";
import { Profession } from "../redux/professionAction";
import ProfessionSelect from "./ProfessionInput";

export default function StructureForm() {
    const [snackBarMessage, setSnackBarMessage] = useState<any | undefined>(
        undefined
    );
    const [isSnackBarOpen, setIsSnackBarOpen] = useState<boolean>(false);
    const [isSuccesSignUp, setIsSuccesSignUp] = useState<boolean>(false);
    const [avatar, setAvatar] = useState<AvatarResponse>({
        contentUrl: "",
        id: "",
    });
    const [image, setImage] = useState<any>([]);
    const [displayAvatar, setDisplayAvatar] = useState<boolean>(false);
    const [legacyChecked, setLegacyChecked] = useState<boolean>(false);
    const [errorLegacy, setErrorLegacy] = useState<boolean>(false);
    const [profession, setProfession] = useState<Profession[]>([]);
    const [userInfos, setUserInfos] = useState<UserProfessionnelResponse>(
        emptyUserProfessionnel
    );
    const [passwordValidated, setPasswordValidated] = useState<boolean>(false);
    const [selectedCountry, setSelectedCountry] = useState<Country | null>(
        null
    );

    const dispatch = useAppDispatch();
    const initAvatar = { contentUrl: "", id: "" };
    const professionnelSelector = useAppSelector(
        (state: RootState) => state.signUpProfessionnel
    );
    const { t } = useTranslation();
    const lang: LanguageType = language();
    const navigate = useNavigate();

    const onSuccesSignUp = () => {
        navigate(RouteUtils.LOGIN);
    };

    const handleSubmit = (event: any) => {
        event.preventDefault();
        if (legacyChecked !== true) {
            setErrorLegacy(true);
            return;
        }
        setErrorLegacy(false);
        const form = new FormData(event.currentTarget);
        let data = {
            email: form.get("email") as string,
            plainPassword: form.get("password") as string,
            telephone: form.get("phone") as string,
            nom: form.get("nom") as string,
            prenom: form.get("prenom") as string,
            country: selectedCountry?.id
                ? `${RouteAPI.COUNTRY}/${selectedCountry.id}`
                : "",
            profession: profession.map(
                (prof) => `${RouteAPI.PROFESSIONS}/${prof.id.toString()}`
            ),
        };
        if (avatar.id.length > 0)
            Object.assign(
                data,
                Object(avatar.id.length) > 1 && {
                    profileImage: RouteAPI.GET_MEDIA_OBJECTS_ID + avatar.id,
                }
            );

        if (!passwordValidated) {
            setSnackBarMessage({
                detail: t("error.password.requirements"),
                status: 400,
            });
            return;
        }

        if (!data.email) {
            setSnackBarMessage({
                detail: t("profileForm.validation.email"),
                status: 400,
            });
            return;
        }

        if (!data.telephone) {
            setSnackBarMessage({
                detail: t("profileForm.validation.phone"),
                status: 400,
            });
            return;
        }

        if (!data.prenom || !data.nom) {
            setSnackBarMessage({
                detail:
                    t("profileForm.validation.firstname") +
                    " - " +
                    t("profileForm.validation.lastname"),
                status: 400,
            });
            return;
        }

        if (!data.country) {
            setSnackBarMessage({
                detail: t("profileForm.validation.country"),
                status: 400,
            });
            return;
        }

        if (data.profession?.length === 0) {
            setSnackBarMessage({
                detail: t("profileForm.validation.profession"),
                status: 400,
            });
            return;
        }

        dispatch(signUpProfessionnel(data as SignUpData))
            .unwrap()
            .then(() => {
                setIsSuccesSignUp(true);
            })
            .catch((error: any) => {
                const serverMessage = error?.response?.data?.message;

                if (error?.response?.status === 422 && serverMessage) {
                    const isDuplicatedEmail = serverMessage.includes("email");

                    setSnackBarMessage({
                        detail: isDuplicatedEmail
                            ? t("profileForm.validation.duplicatedEmail")
                            : serverMessage,
                        status: 422,
                    });
                } else {
                    setSnackBarMessage({
                        detail: "Une erreur est survenue. Veuillez réessayer.",
                        status: 500,
                    });
                }

                setIsSnackBarOpen(true);
            });
    };

    const deleteAvatar = () => {
        setAvatar(initAvatar);
        dispatch(clearPicture());
        setDisplayAvatar(false);
    };

    const onChange = (image: ImageType) => {
        if (image[0]) {
            dispatch(postAvatar(image[0].file));
            setImage(image);
            setDisplayAvatar(true);
        }
    };

    useEffect(() => {
        if (professionnelSelector.data.profileImage) {
            setAvatar(professionnelSelector.data.profileImage);
        }
        if (professionnelSelector.error) {
            setSnackBarMessage(professionnelSelector.error.response?.data);
            setIsSnackBarOpen(true);
        }
        if (professionnelSelector.data) {
            setUserInfos(professionnelSelector.data);
        }
        return setIsSnackBarOpen(false);
    }, [professionnelSelector]);

    useEffect(() => {
        if (userInfos.id) setIsSuccesSignUp(true);
    }, [userInfos]);

    useEffect(() => {
        setIsSnackBarOpen(false);
        setSnackBarMessage(undefined);
    }, []);

    const legacyCheckboxLink = () => {
        return (
            <span onClick={() => navigate(RouteApp.LEGACY)}>
                <u>{t("sign.up.condition")}</u>
            </span>
        );
    };

    return (
        <Container component="main" maxWidth="xs">
            <CssBaseline />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Box
                    component="form"
                    noValidate
                    onSubmit={handleSubmit}
                    sx={{ mt: 2 }}
                >
                    <Box sx={{ mb: 2 }}>
                        <ImageUploading
                            value={image}
                            onChange={(img) => onChange(img)}
                        >
                            {({
                                onImageUpload,
                                onImageUpdate,
                                isDragging,
                                dragProps,
                            }) => (
                                <Stack className="upload__image-wrapper">
                                    <Button
                                        variant="outlined"
                                        sx={{ alignSelf: "center" }}
                                        style={
                                            isDragging
                                                ? { color: "red" }
                                                : undefined
                                        }
                                        onClick={onImageUpload}
                                        {...dragProps}
                                    >
                                        {t("sign.up.avatar")}
                                    </Button>
                                    {displayAvatar &&
                                        avatar.contentUrl.length > 0 && (
                                            <div
                                                className="image-item"
                                                style={{
                                                    textAlign: "center",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    flexDirection: "column",
                                                }}
                                            >
                                                <Avatar
                                                    src={
                                                        avatar.contentUrl
                                                            ? getPictureFromProfilImage(
                                                                  avatar.contentUrl
                                                              )
                                                            : undefined
                                                    }
                                                    sx={{
                                                        width: 94,
                                                        height: 94,
                                                        m: 1,
                                                    }}
                                                    variant="square"
                                                    sizes="large"
                                                />
                                                <div className="image-item__btn-wrapper">
                                                    <Button
                                                        variant="outlined"
                                                        onClick={() =>
                                                            onImageUpdate(0)
                                                        }
                                                        sx={{ mr: 1 }}
                                                    >
                                                        {t(
                                                            "sign.up.avatar.button.modify"
                                                        )}
                                                    </Button>
                                                    <Button
                                                        sx={{ px: 2.8 }}
                                                        variant="outlined"
                                                        onClick={() =>
                                                            deleteAvatar()
                                                        }
                                                    >
                                                        {t(
                                                            "sign.up.avatar.button.delete"
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    {professionnelSelector.isExtraField &&
                                        avatar.contentUrl.length <= 0 && (
                                            <Box sx={{ alignSelf: "center" }}>
                                                <InfinitySpin
                                                    color="#A93F58"
                                                    width="200"
                                                />
                                            </Box>
                                        )}
                                </Stack>
                            )}
                        </ImageUploading>
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                autoComplete="given-name"
                                name="nom"
                                required
                                fullWidth
                                id="nom"
                                label={t("sign.up.lastname")}
                                autoFocus
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                id="prenom"
                                label={t("sign.up.firstname")}
                                name="prenom"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <ProfessionSelect
                                label={t("sign.up.profession")}
                                value={profession}
                                onChange={setProfession}
                                multiple={true}
                                required
                                sx={{
                                    width: "100%",
                                    backgroundColor: "white",
                                    borderRadius: "5px",
                                }}
                                name="profession"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CountrySelect
                                label={t("sign.up.country")}
                                value={selectedCountry}
                                onChange={setSelectedCountry}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                type="number"
                                id="phone"
                                label={t("sign.up.phone")}
                                name="phone"
                                autoComplete="tel-national"
                                sx={{
                                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                        {
                                            display: "none",
                                        },
                                    "& input[type=number]": {
                                        MozAppearance: "textfield",
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                id="email"
                                label={t("auth.email")}
                                name="email"
                                autoComplete="email"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <PasswordInput onValidate={setPasswordValidated} />
                        </Grid>
                    </Grid>
                    <FormControl
                        required
                        error={errorLegacy}
                        component="fieldset"
                        sx={{ mt: 3 }}
                        variant="standard"
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={legacyChecked}
                                    onChange={() =>
                                        setLegacyChecked(!legacyChecked)
                                    }
                                    name="terms"
                                />
                            }
                            label={
                                <>
                                    <span>{t("sign.up.therms")}</span>{" "}
                                    {legacyCheckboxLink()}
                                </>
                            }
                        />
                        {errorLegacy && (
                            <FormHelperText>
                                {t("sign.up.error")}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                        color="error"
                    >
                        {t("sign.up.button.register")}
                    </Button>
                    {isSnackBarOpen || snackBarMessage ? (
                        <Alert severity="error" className="errorMessage">
                            {snackBarMessage?.detail
                                ? snackBarMessage?.detail
                                : ""}
                        </Alert>
                    ) : null}
                    <Grid
                        container
                        justifyContent="flex-end"
                        marginBottom="1rem"
                    >
                        <Grid item>
                            <Link href={RouteUtils.LOGIN} variant="body2">
                                {t("sign.up.connection")}
                            </Link>
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            <Modal
                open={isSuccesSignUp}
                title={lang.titleModalSignUpSuccess}
                message={lang.messageModalSignUpSucces}
                onClose={() => onSuccesSignUp()}
                onValidation={() => onSuccesSignUp()}
                titleButtonValidation={"Valider"}
            />
        </Container>
    );
}
